import { concat, find, includes, last, map, without } from 'lodash/fp';
import { H2, Hidden, Image, SpacerWrapper, Wrapper } from '@ahmdigital/ui';
import classNames from 'classnames';
import React from 'react';
import styled from 'styled-components';

import { LimitGroup } from '../../types';
import boneImage from './images/bone.svg';
import constants from '../../../../../../ahm-constants';
import glassesImage from './images/glasses.svg';
import handRightImage from '../../../../../../images/hand-right.svg';
import ServiceGroupPanel from '../service-group-panel';
import SnakeSeparator from '../snake-separator';
import styles from './styles.module.sass';
import zenTreeImage from './images/zen-tree.svg';

const {
  PRODUCT_LIMIT_CATEGORY: { MUSCLE_BONE, OPTICAL, WELLBEING },
} = constants;

const PanelImage = styled(Image)`
  max-width: initial;
  transform: ${({ $isCondensed }) => ($isCondensed ? 'scale(0.8)' : 'none')};
`;

const GlassesImage = styled(PanelImage)`
  position: absolute;
  top: 75px;
  right: ${({ $isCondensed }) => ($isCondensed ? '190px' : '220px')};
`;

const BoneImage = styled(PanelImage)`
  transform: ${({ $isCondensed }) => ($isCondensed ? 'rotate(-10deg) scale(0.8)' : 'rotate(-10deg)')};
`;

const StyledSection = styled.section`
  position: relative;
`;

const AnchorId = styled.div`
  position: absolute;
  top: -1.5rem;
`;

const getBundles = (isCondensed?: boolean) => [
  {
    id: MUSCLE_BONE,
    image: <BoneImage $isCondensed={isCondensed} src={boneImage.src} alt="Illustration of a bone" />,
  },
  {
    id: OPTICAL,
    image: (
      <>
        <Hidden sm xs>
          <GlassesImage $isCondensed={isCondensed} src={glassesImage.src} alt="Illustration of glasses" />
        </Hidden>
        <Hidden df lg md>
          <PanelImage $isCondensed={isCondensed} src={glassesImage.src} alt="Illustration of glasses" />
        </Hidden>
      </>
    ),
  },
  {
    id: WELLBEING,
    image: <PanelImage $isCondensed={isCondensed} src={zenTreeImage.src} alt="Illustration of a bonsai tree" />,
  },
];

type Bundle = string;

type BundlesSectionProps = {
  allIncludedServices?: LimitGroup[];
  isCondensed?: boolean;
  scale: string;
  selectedBundles: Bundle[];
  setSelectedBundles: (bundles: Bundle[]) => void;
};

const BundlesSection = ({
  allIncludedServices,
  isCondensed,
  scale,
  selectedBundles,
  setSelectedBundles,
}: BundlesSectionProps): JSX.Element => {
  const onChange = (id: Bundle) => {
    const isAlreadySelected = includes(id, selectedBundles);
    const processSelection = isAlreadySelected ? without : (arg1: Bundle[], arg2: Bundle[]) => concat(arg2, arg1);
    const newSelectedBundles = processSelection([id], selectedBundles) as Bundle[];

    setSelectedBundles(newSelectedBundles);
  };

  const bundles = getBundles(isCondensed);

  return (
    <StyledSection>
      <AnchorId id="bundles-section" />
      <Wrapper size="large">
        <SpacerWrapper variant={isCondensed ? 'paddingBottomSpacer9' : 'paddingBottomSpacer7'}>
          {!isCondensed && (
            <>
              <Image src={handRightImage.src} alt="Illustration of a hand" className={styles.handImageTop} />
              <Image src={handRightImage.src} alt="Illustration of a hand" className={styles.handImageBottom} />
            </>
          )}
          <H2 looksLike="h3" variant={['marginBottomSpacer9', 'center']}>
            Now add up to 3 bundles
          </H2>
        </SpacerWrapper>
      </Wrapper>

      {map(({ id, image }) => {
        const isLastPanel = id === last(bundles)?.id;
        const includedServices = find({ limitId: id }, allIncludedServices);
        if (!includedServices) {
          return null;
        }
        const isSelected = includes(id, selectedBundles);
        return (
          <div
            // @ts-expect-error - Automatic, Please fix when editing this file
            className={classNames(styles.serviceGroupPanel, { [styles.isCondensed]: isCondensed })}
            data-test={id}
            key={id}
          >
            <ServiceGroupPanel
              id={id}
              isSelected={isSelected}
              image={image}
              limits={includedServices.limits}
              onClick={() => onChange(id)}
              scale={scale}
              services={includedServices.services}
              title={includedServices.displayName}
            />
            {!isLastPanel && !isCondensed && <SnakeSeparator hideOn={['xs', 'sm']} />}
          </div>
        );
      }, bundles)}
    </StyledSection>
  );
};

BundlesSection.defaultProps = {
  allIncludedServices: [],
  isCondensed: false,
};

export default BundlesSection;
